import type { GameState } from "./GameState";
import type { ChapterData } from "../data/content-types";
import type { MissionObjective } from "../core/types";
import { bus } from "../core/EventBus";

export interface ObjectiveView {
  objective: MissionObjective;
  done: boolean;
  progress: string;
}

/**
 * Misiones multiobjetivo (punto 22). Los objetivos se evalúan a partir del
 * estado del capítulo; los objetivos `hidden` se revelan al completar los
 * anteriores (descubrimiento progresivo).
 */
export class MissionManager {
  constructor(private state: GameState, private chapter: ChapterData) {}

  private prog() {
    return this.state.chapter(this.chapter.location);
  }

  get chapterTitle(): string {
    return this.chapter.title;
  }

  start(): void {
    bus.emit("MISSION_STARTED", { missionId: this.chapter.mission.id });
  }

  private get needTalk(): number {
    return this.chapter.needTalk ?? 2;
  }

  private get needHazards(): number {
    return this.chapter.needHazards ?? 3;
  }

  isObjectiveDone(obj: MissionObjective): boolean {
    const p = this.prog();
    switch (obj.type) {
      case "talk_workers":
        return p.talked.length >= Number(obj.target ?? this.needTalk);
      case "discover_hazards":
        return p.hazards.length >= Number(obj.target ?? this.needHazards);
      case "extra":
        return p.extraDone;
      case "equip":
        return p.equipped;
      case "report":
        return p.done;
      default:
        return false;
    }
  }

  private objProgress(obj: MissionObjective): string {
    const p = this.prog();
    switch (obj.type) {
      case "talk_workers":
        return `${Math.min(p.talked.length, Number(obj.target ?? 1))}/${obj.target}`;
      case "discover_hazards":
        return `${Math.min(p.hazards.length, Number(obj.target ?? 1))}/${obj.target}`;
      default:
        return "";
    }
  }

  /** Objetivos visibles: los no ocultos, y los ocultos cuyos previos ya están hechos. */
  visibleObjectives(): ObjectiveView[] {
    const views: ObjectiveView[] = [];
    let allPrevDone = true;
    for (const obj of this.chapter.mission.objectives) {
      if (obj.hidden && !allPrevDone) break;
      const done = this.isObjectiveDone(obj);
      views.push({ objective: obj, done, progress: this.objProgress(obj) });
      allPrevDone = allPrevDone && done;
    }
    return views;
  }

  /** ¿Están listos los requisitos para poder trabajar y cerrar el caso? */
  canReport(): boolean {
    const p = this.prog();
    const extraOk = !this.chapter.extra || p.extraDone;
    return (
      p.talked.length >= this.needTalk &&
      p.hazards.length >= this.needHazards &&
      p.equipped &&
      extraOk
    );
  }

  markReport(): boolean {
    if (!this.canReport()) return false;
    const p = this.prog();
    if (p.done) return false;
    p.done = true;
    this.state.persist();
    bus.emit("MISSION_COMPLETED", { missionId: this.chapter.mission.id });
    return true;
  }

  isComplete(): boolean {
    return this.prog().done;
  }
}
